import React, { useState, useEffect, useCallback, FormEvent, ReactNode } from 'react'
import { NavigateFn } from '@reach/router'
import lowerCase from 'lodash/fp/lowerCase'
import replace from 'lodash/fp/replace'
import compose from 'lodash/fp/compose'
import omitBy from 'lodash/fp/omitBy'
import isNull from 'lodash/fp/isNull'
import moment from 'moment-timezone'
import cx from 'classnames'
import { ApolloError } from 'apollo-boost'
import { useDropzone } from 'react-dropzone'
import track from '@track'
import Event from '@track/Event'
import {
  Switch,
  Divider,
  ControlGroup,
  InputGroup,
  Collapse,
  RadioGroup,
  Radio,
  Intent,
  TextArea,
  Checkbox,
  FormGroup,
} from '@blueprintjs/core'
import {
  Button,
  Icon,
  MultiEmployeeSelect,
  Toaster,
  Details,
  Skeleton,
  LinkButton,
  HoursSelect,
  PercentSelect,
  ConfirmBox,
  TimeCountSelect,
  createTimeOptions,
  MultiHRLocationSelect,
  List,
  Flex,
  IconButton,
  Item,
} from '@ui'
import { navigateToAphrodite } from '@utils/navigateToAphrodite'
import { legacyMode } from 'App'
import usePasswordPolicies from './hooks/usePasswordPolicies'
import useSettings from './hooks/useSettings'
import useFileReader from './hooks/useFileReader'
import { useUser } from '@hooks'
import api, { ICompanyAsset, IEmployee, Role, IUserSettings, ISettings, ISpaceType } from '@api'
import { useAppData } from '@providers'
import { useReload } from '@hooks/reloadContext'
import useWorkingHoursLocationGroups from './hooks/useWorkingHoursLocationGroups'
import LocationCapacities from './LocationCapacities'
import LocationHealthChecks from './LocationHealthChecks2'
import LocationBookableDesk, { BookableDeskEdit } from './LocationBookableDesk'
import { get, omit } from 'lodash'
import { bookableDesks, capitalizeStr } from '@utils'
import { timeList } from '@ui/selects/HoursSelect'
import useSanitize from '@hooks/useSanitize'
import LocationWorkingHours from './LocationWorkingHours'
import TimeSelector from './TimeSelector'
import getFormattedTimeWithAddedMinutes from './utils/getFormattedTimeWithAddedMinutes'
import { PasswordRequirement } from 'components/Settings'
import css from './settings.module.scss'
import { getDefaultTimes } from '@utils'
import image from 'assets/logo-spaceIQ-endorsement.svg'
import { SelectWrapper } from 'components/AdminFloorPlan/components/SelectWrapper'
import MultiSpaceTypeSelect from '@ui/selects/MultiSpaceTypeSelect'
import { useSpaceTypes } from '@ui/selects/SpaceTypeSelect'
import MultiHRLocationList from '@ui/selects/MultiHRLocationList'
import { useHRLocations } from '@ui/selects/HRLocationList'
import { useSplitTreatments } from '@splitsoftware/splitio-react'

enum BookingTypes {
  HOURLY_ONLY = 'Hourly Only',
  ALL_DAY_ONLY = 'All Day Only',
  BOTH = 'Both',
}

interface SettingCategory {
  name: string
  id: string
  permitted: Role[]
  settings: Setting[]
  leftIcon?: string
}

interface Setting {
  name: string
  description: string
  permitted?: Role[]
  className?: string
  superUserOnly?: boolean
  feature?: boolean
  isPostDescriptionGrey?: boolean
  action?: React.ReactNode // used for showing small link text or buttons
  state?: React.ReactNode // used for showing what is currently
  postDescription?: React.ReactNode // used for showing an extra collapsable section under the standard section
  attachedSettings?: React.ReactNode // similar to above, but used for always shown when toggled on sections
}

const replaceSpaces = compose(replace(' ', '-'), lowerCase)

const getCurrentBookingType = ({
  dailyBookingEnabled,
  hourlyBookingEnabled,
}: {
  dailyBookingEnabled: boolean
  hourlyBookingEnabled: boolean
}) =>
  hourlyBookingEnabled && dailyBookingEnabled
    ? BookingTypes.BOTH
    : hourlyBookingEnabled && !dailyBookingEnabled
    ? BookingTypes.HOURLY_ONLY
    : BookingTypes.ALL_DAY_ONLY

const getCurrentBookingRestriction = ({
  hotelingDeskDeptEnforcementEnabled,
  hotelingDeskWpgEnforcementEnabled,
  hotelingDeskWpgEnforcementOperator,
}: {
  hotelingDeskDeptEnforcementEnabled: boolean
  hotelingDeskWpgEnforcementEnabled: boolean
  hotelingDeskWpgEnforcementOperator: string
}) =>
  hotelingDeskDeptEnforcementEnabled && hotelingDeskWpgEnforcementEnabled
    ? `Department ${hotelingDeskWpgEnforcementOperator} WPG`
    : hotelingDeskDeptEnforcementEnabled
    ? 'Department Only'
    : hotelingDeskWpgEnforcementEnabled
    ? 'WPG Only'
    : 'No Restrictions'

type SettingsPageProps = {
  id: string
  navigate?: NavigateFn
}
enum CATEGORIES {
  USER = 'user',
  COMPANY = 'company',
}
export default function Settings({ id, navigate }: SettingsPageProps) {
  const DEFAULT_CATEGORIE = legacyMode ? CATEGORIES.USER : id ? id : CATEGORIES.COMPANY
  const { user } = useUser()
  const reload = useReload()
  const { updateCompanySetting, ...hook } = useSettings()
  const {
    name: companyName,
    id: companyId,
    fromEmail,
    assets,
    product: { features },
  } = useAppData()
  const { file, setFile, fileContent } = useFileReader()
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files: File[]) => setFile(files[0]),
    accept: {
      'image/png': ['.png'],
    },
    maxSize: 100000,
    onDropRejected: () =>
      Toaster.fail({
        message: 'File wrong type (PNG only) or too big (max 100KB)',
      }),
  })
  const spaceTypes = useSpaceTypes().filter((x) => x.interactivity === 'seatable')

  const workingHoursGroupsHook = useWorkingHoursLocationGroups(
    {
      bookingWorkdayTimeFrom: hook.bookingWorkdayTimeFrom,
      bookingWorkdayTimeTo: hook.bookingWorkdayTimeTo,
      locationWorkingHoursGroups: hook.locationWorkingHoursGroups,
    },
    hook.setData
  )
  const [activeCat, setActiveCat] = useState(DEFAULT_CATEGORIE)
  const [shownPostDescriptionName, setShownPostDescriptionName] = useState('')
  const policies = usePasswordPolicies()
  const [oldPassword, setOldPassword] = useState('')
  const [newCompanyName, setNewCompanyName] = useState('')
  const [emailAlias, setEmailAlias] = useState(fromEmail)
  const [error, setError] = useState(false)
  const [exemptedEmployees, setExemptedEmployees] = useState<IEmployee[]>([])
  const [healthConfirmationMessage, setHealthConfirmationMessage] = useState(
    hook.healthScreeningConfirmButtonText
  )

  const [healthScreenMessage, setHealthScreenMessage] = useState(hook.healthScreeningMessage)
  const [isUpdatingScreening, setIsUpdatingScreening] = useState(false)
  const [isSavingScreening, setIsSavingScreening] = useState(false)
  const [showConfirmBox, setShowConfirmBox] = useState(false)
  const [emailNotificationEnabled, setEmailNotificationEnabled] = useState(false)
  const sanitize = useSanitize()
  const requiredDesks = ['hoteling_desk']
  const locations = useHRLocations()
  const featureFlagName = 'company-email-rebrand'
  const { treatments } = useSplitTreatments({ names: [featureFlagName] })

  useEffect(() => {
    setActiveCat(DEFAULT_CATEGORIE)
  }, [DEFAULT_CATEGORIE])

  useEffect(
    useCallback(() => {
      hook.setData({ user: { ...user } })
    }, [hook, user]),
    [user]
  )

  useEffect(() => {
    setEmailNotificationEnabled(
      hook.bookingReminderEnabled ||
        hook.bookingConfirmationEnabled ||
        hook.bookingCancellationEnabled ||
        hook.bookingConfirmationOnbehalfEnabled ||
        hook.bookingCancellationOnbehalfEnabled ||
        hook.bookingCheckinReminderOnbehalfEnabled
    )
  }, [
    hook.bookingReminderEnabled,
    hook.bookingConfirmationEnabled,
    hook.bookingCancellationEnabled,
    hook.bookingConfirmationOnbehalfEnabled,
    hook.bookingCancellationOnbehalfEnabled,
    hook.bookingCheckinReminderOnbehalfEnabled,
    setEmailNotificationEnabled,
  ])

  useEffect(() => {
    setHealthConfirmationMessage(hook.healthScreeningConfirmButtonText)
    setHealthScreenMessage(hook.healthScreeningMessage)
  }, [hook.healthScreeningConfirmButtonText, hook.healthScreeningMessage])

  useEffect(() => {
    api.employee.exemptFromCheckin().then((exempt) => setExemptedEmployees(exempt))
  }, [])

  const userRole = user?.employee.role ?? Role.VIEWER

  const assetUrl = assets.length ? assets[assets.length - 1].url : null
  const imgSrc = fileContent.length ? fileContent : assetUrl

  const { timeFrom, timeTo } = getDefaultTimes(
    hook.bookingWorkdayTimeFrom,
    hook.bookingWorkdayTimeTo
  )

  const timezone = 'Etc/UTC'
  const dateTimeList = createTimeOptions(timezone)

  const submitPassword = (e: FormEvent) => {
    e.preventDefault()

    if (!policies.isValid) return
    api.employee.password
      .update(oldPassword, policies.password)
      .then((message: ReactNode) => {
        Toaster.success({ message })
        setShownPostDescriptionName('')
      })
      .catch((error: ApolloError) => Toaster.fail({ error }))
  }

  const updateUserSettings = (settings: Partial<{ [key in keyof IUserSettings]: unknown }>) => {
    const old = user?.context.settings
    hook.setPath('user.context.settings', settings)
    api.user
      .updateSettings(settings)
      .then(() => {
        Toaster.success({
          message: 'User settings updated.',
        })
      })
      .catch((error: ApolloError) => {
        hook.setPath('user.settings', old)
        Toaster.fail({ error })
      })
  }

  const updateUnits = (unit: 'sqft' | 'sqm') => () => {
    const old = user?.context.units
    hook.setPath('user.context.units', unit)
    api.user
      .updateUnits(user?.id as string, unit)
      .then(() => {
        Toaster.success({
          message: `Updated perferred units to ${unit}.`,
        })
      })
      .catch((error: ApolloError) => {
        hook.setPath('user.context.units', old)
        Toaster.fail({ error })
      })
  }

  const uploadAsset = () =>
    assets.length
      ? api.company.asset.update(assets[0].id, fileContent)
      : api.company.asset.create(fileContent)

  const fetchRestrictedSpaceTypes = () => {
    const filteredSpaceTypes = (hook?.overriddenBookingRestrictionSpaceTypes || []).map(
      (value: ISpaceType | string) => {
        const spaceType = spaceTypes
          ? spaceTypes.find((spaceType: ISpaceType) => spaceType?.name === value)
          : {}
        return spaceType
      }
    )

    return filteredSpaceTypes
  }
  const validateEmail = (email: string) => {
    if (email?.length === 0) return false
    // Regular expression for basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return !emailRegex.test(email)
  }

  const userSettings: Setting[] = [
    {
      name: 'Change Password',
      description: 'Choose a unique password to protect your account',
      action: (
        <div
          className="text-primary hover:underline cursor-pointer mt-2"
          onClick={() =>
            setShownPostDescriptionName(
              shownPostDescriptionName === 'Change Password' ? '' : 'Change Password'
            )
          }>
          {shownPostDescriptionName === 'Change Password' ? 'CLOSE' : 'CHANGE'}
        </div>
      ),
      postDescription: (
        <form onSubmit={submitPassword}>
          <Details.Password
            editing
            required
            label="Old Password"
            onChange={(e) => setOldPassword(e.target.value)}
            value={oldPassword}
          />
          New Password Must:
          {policies.fetchingPolicies && <Skeleton />}
          {!policies.fetchingPolicies && (
            <ul className=" list-none p-0 mt-1">
              {policies.list.map((p, i) => (
                <PasswordRequirement key={i} pass={p.complete}>
                  {p.message}
                </PasswordRequirement>
              ))}
            </ul>
          )}
          <Details.Password
            editing
            required
            label="New Password"
            onChange={(e) => policies.setPassword(e.target.value)}
            value={policies.password}
          />
          <Details.Password
            editing
            required
            label="Confirm Password"
            onChange={(e) => policies.setConfirm(e.target.value)}
            value={policies.confirm}
          />
          <div className="flex">
            <Button
              className="mla"
              disabled={!policies.isValid}
              intent={Intent.PRIMARY}
              text="save"
              type="submit"
            />
          </div>
        </form>
      ),
    },
    {
      name: 'Units',
      description: 'Select which area units are shown within Eptura',
      permitted: [
        Role.ADMIN,
        Role.POWER_ADMIN,
        Role.DEPARTMENT_ADMIN,
        Role.IT,
        Role.LOCATION_ADMIN,
        Role.SPACE_CAPTAIN,
        Role.BOOKINGS_MANAGER,
      ],
      action: (
        <ControlGroup className="mt-1">
          <Button
            disabled={!hook.user}
            style={{ borderRadius: '4px 0 0 4px' }}
            onClick={updateUnits('sqft')}
            intent={user?.context.units === 'sqft' ? Intent.PRIMARY : Intent.NONE}
            text="Sqft"
          />
          <Button
            disabled={!hook.user}
            style={{ borderRadius: '0 4px 4px 0' }}
            onClick={updateUnits('sqm')}
            intent={user?.context.units === 'sqm' ? Intent.PRIMARY : Intent.NONE}
            text={
              <>
                M<sup>2</sup>
              </>
            }
          />
        </ControlGroup>
      ),
    },
    {
      name: 'Health Check Notifications',
      description:
        hook.enableHealthScreening && hook.healthNoticeEnabled
          ? "Receive email notification when an employee health check isn't completed."
          : 'Admin health check alerts have been disabled.',
      feature: features.includes('healthCheckAcknowledgment'),
      permitted: [Role.ADMIN, Role.POWER_ADMIN, Role.DEPARTMENT_ADMIN, Role.LOCATION_ADMIN],
      action: (
        <Switch
          large
          innerLabelChecked="ON"
          innerLabel="OFF"
          checked={
            hook.enableHealthScreening &&
            hook.healthNoticeEnabled &&
            user?.context.settings.healthNoticeEnabled
          }
          disabled={!(hook.enableHealthScreening && hook.healthNoticeEnabled)}
          onChange={({ target: { checked: healthNoticeEnabled } }: any) =>
            updateUserSettings({ healthNoticeEnabled })
          }
        />
      ),
    },
    {
      name: 'Locations',
      description: 'Customize your preferred locations',
      feature: features.includes('healthCheckAcknowledgment'),
      permitted: [Role.ADMIN, Role.POWER_ADMIN, Role.DEPARTMENT_ADMIN, Role.LOCATION_ADMIN],
      action: (
        <div
          className="text-primary hover:underline cursor-pointer mt-2"
          onClick={() =>
            setShownPostDescriptionName(shownPostDescriptionName === 'Locations' ? '' : 'Locations')
          }>
          {shownPostDescriptionName === 'Locations' ? 'CLOSE' : 'CHANGE'}
        </div>
      ),
      postDescription: (
        <div>
          <MultiHRLocationList
            values={
              get(hook, 'user.employee.preferredLocations', []).length === 0
                ? locations
                : get(hook, 'user.employee.preferredLocations', [])
            }
            onChange={(locations) => {
              if (!hook.user) return
              const path = 'user.employee.preferredLocations'
              const old = get(hook, path, [])
              hook.setPath(path, locations)
              api.employee.preferredLocations
                .update(
                  get(hook, 'user.employee.id', ''),
                  locations.map((l) => l.id)
                )
                .then((employee) => {
                  hook.setPath(path, employee.preferredLocations)
                  Toaster.success({
                    message: 'HR Locations Saved.',
                  })
                })
                .catch((error: ApolloError) => {
                  hook.setPath(path, old)
                  Toaster.fail({ error })
                })
            }}
            cancelLocationFilter={() => setShownPostDescriptionName('')}
          />
        </div>
      ),
    },
  ]

  const companySettings: Setting[] = [
    {
      name: 'Company Branding',
      description: 'Update Company Name & Company Logo',
      action: (
        <div
          className="text-primary hover:underline cursor-pointer mt-2"
          onClick={() =>
            setShownPostDescriptionName(
              shownPostDescriptionName === 'Company Branding' ? '' : 'Company Branding'
            )
          }>
          {shownPostDescriptionName === 'Company Branding' ? 'CLOSE' : 'CHANGE'}
        </div>
      ),
      postDescription: (
        <div className="flex flex-col max-w-full justify-center">
          <InputGroup
            style={{ width: 400 }}
            value={newCompanyName}
            placeholder={companyName}
            onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
              setNewCompanyName(value)
            }
          />
          <div>
            <div
              className="flex flex-row items-center justify-center bg-grey-200 rounded mt-3"
              {...getRootProps()}
              style={{
                height: 60,
                width: 400,
                border: `2px dashed ${isDragActive ? 'var(--border-color)' : '#f1f3f4'}`,
              }}>
              <input {...getInputProps()} />
              <Icon
                name="file-upload"
                style={{
                  fontSize: 34,
                  color: isDragActive ? 'var(--primary-color)' : 'var(--grey)',
                }}
              />
              <div className="flex flex-row" style={{ textAlign: 'center' }}>
                {file ? `File: ${file.name} ` : ''}
                {file ? (
                  <p className="text-xs cursor-default text-grey-500 flex items-center">
                    click to select new file
                  </p>
                ) : (
                  <p>
                    Drag and drop or <LinkButton text=" Browse " /> your Logo
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="flex mt-3">
            <div className=" flex flex-col" style={{ width: 400 }}>
              <div className=" text-grey-700">Preview</div>
              <div style={{ display: 'flex', marginBottom: 15 }}>
                <div
                  style={{
                    height: 60,
                    fontSize: 16,
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    background: '#f5f6f9',
                  }}>
                  <span className={css.bar_icon}>
                    <Icon name="light fa-bars" light={true}></Icon>
                  </span>
                  <img alt="" style={{ maxHeight: 30 }} src={image} />
                  <div className={css.text}>for</div>
                  <div className={css.logo_container}>
                    {!imgSrc && (
                      <div
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                        {newCompanyName || companyName}
                      </div>
                    )}
                    {imgSrc && (
                      <img
                        data-cy="light_preview"
                        alt="light_logo_preview"
                        src={imgSrc}
                        className={css.logo_preview}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between mt-2">
            <Button
              text="Reset to Default"
              disabled={!assets.length}
              onClick={() =>
                assets.length &&
                api.company.asset.remove(assets[0].id).then(() => {
                  reload.appData()
                  setFile(null)
                  Toaster.success({ message: 'Successfully removed Logo' })
                })
              }
            />
            <Button
              disabled={!newCompanyName && !fileContent}
              intent="primary"
              text="Save"
              onClick={() =>
                newCompanyName.length
                  ? api.company.name
                      .update({
                        id: companyId,
                        newCompanyName: sanitize(newCompanyName).trim(),
                      })
                      .then(() => {
                        fileContent
                          ? uploadAsset()
                              .then((asset: ICompanyAsset) => {
                                hook.setData({ assets: [asset] })
                                Toaster.success({
                                  message: 'Updated Company Name & Logo',
                                })
                                setFile(null)
                                reload.appData()
                              })
                              .catch((error: ApolloError) => Toaster.fail({ error }))
                          : Toaster.success({
                              message: 'Updated Company name',
                            })
                        reload.appData()
                      })
                      .catch((error: ApolloError) => Toaster.fail({ error }))
                  : uploadAsset()
                      .then((asset: ICompanyAsset) => {
                        Toaster.success({ message: 'Updated Company Logo' })
                        hook.setData({ assets: [asset] })
                        setFile(null)
                        reload.appData()
                      })
                      .catch((error: ApolloError) => Toaster.fail({ error }))
              }
            />
          </div>
        </div>
      ),
    },
    {
      name: 'Booking Alias Email',
      permitted: [Role.ADMIN, Role.POWER_ADMIN],
      description:
        'Enter a custom email which will be used to mask the sender email address for booking-related email notifications. If no email address is specified, no-reply@spaceiq.com will be used to send emails.',
      action: shownPostDescriptionName !== 'Booking Alias Email' && (
        <Button
          className="mr-3"
          minimal
          blue
          text="Edit"
          iconName="pencil"
          onClick={() =>
            setShownPostDescriptionName(
              shownPostDescriptionName === 'Booking Alias Email' ? '' : 'Booking Alias Email'
            )
          }
        />
      ),
      postDescription: (
        <Flex justify="space-between">
          <div>
            <FormGroup
              helperText={error && 'Invalid email address'}
              intent={error ? Intent.DANGER : Intent.NONE}>
              <InputGroup
                style={{ width: 300 }}
                placeholder={'Enter email alias'}
                type="email"
                name="email"
                intent={error ? Intent.DANGER : Intent.NONE}
                value={emailAlias}
                onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
                  setError(validateEmail(value))
                  setEmailAlias(value)
                }}
              />
            </FormGroup>
          </div>
          <div>
            <Button
              className="mr-3"
              minimal
              text="Cancel"
              iconName="times"
              onClick={() =>
                setShownPostDescriptionName(
                  shownPostDescriptionName === 'email alias' ? '' : 'email alias'
                )
              }
            />

            <Button
              minimal
              text="Save"
              iconName="check"
              intent="primary"
              disabled={error}
              onClick={() => {
                api.company.emailAlias
                  .updateEmailAlias({
                    fromEmail: emailAlias.length ? emailAlias : null,
                    id: companyId,
                  })
                  .then(() =>
                    Toaster.success({
                      message: 'Updated Company Email',
                    })
                  )
                setShownPostDescriptionName(
                  shownPostDescriptionName === 'email alias' ? '' : 'email alias'
                )
              }}
            />
          </div>
        </Flex>
      ),
    },
    {
      name: 'Approval threshold for employee imports',
      permitted: [Role.ADMIN],
      description:
        'Set up a threshold for terminations/unseated employees to trigger the workflow import.',
      action: (
        <Switch
          large
          innerLabelChecked="ON"
          innerLabel="OFF"
          checked={hook.employeeImportApprovalEnabled}
          onChange={() =>
            updateCompanySetting({
              employeeImportApprovalEnabled: !hook.employeeImportApprovalEnabled,
            })
          }
        />
      ),
      attachedSettings: hook.employeeImportApprovalEnabled && (
        <>
          <div style={{ marginBottom: 16 }} className="flex">
            <div
              className="text-grey-700"
              style={{
                marginRight: 20,
                marginBottom: 10,
                width: 550,
                flexGrow: 1,
              }}>
              <div className="text-lg leading-normal text-grey-700 font-medium mb-2">
                Set up approval threshold level, where:
              </div>
              <div>{'if < x%, the employee import will be automatically performed'}</div>
              <div>{'if > x%, manual approval will be required'}</div>
            </div>
            <div className="flex items-start" style={{ width: 302.25 }}>
              <PercentSelect
                value={hook.employeeImportApprovalThresholdPercent}
                onChange={(opt) =>
                  updateCompanySetting({ employeeImportApprovalThresholdPercent: opt.value })
                }
              />
            </div>
          </div>
        </>
      ),
    },
    {
      name: 'Unseat Allocation Behavior',
      description: 'Unallocate space when employee is unseated.',
      action: (
        <Switch
          large
          innerLabelChecked="ON"
          innerLabel="OFF"
          checked={hook.unallocateSpaceOnUnseat}
          onChange={() =>
            updateCompanySetting({
              unallocateSpaceOnUnseat: !hook.unallocateSpaceOnUnseat,
            })
          }
        />
      ),
    },
    {
      className: css.business_hours_wrapper,
      isPostDescriptionGrey: false,
      name: 'Regular Business Hours',
      description: 'Bookings will be restricted to between these hours globally',
      state: `${timeFrom.format('h:mm a')} - ${timeTo.format('h:mm a')}`,
      action: (
        <div
          className="text-primary hover:underline cursor-pointer mt-2"
          onClick={() =>
            setShownPostDescriptionName(
              shownPostDescriptionName === 'Regular Business Hours' ? '' : 'Regular Business Hours'
            )
          }>
          {shownPostDescriptionName === 'Regular Business Hours' ? 'CLOSE' : 'CHANGE'}
        </div>
      ),
      postDescription: (
        <div>
          <div className="bg-grey-100 rounded p-4 mb-4">
            <TimeSelector
              timeFrom={timeFrom}
              timeTo={timeTo}
              timezone={timezone}
              timeList={dateTimeList}
              disabledToOptions={dateTimeList.reduce<number[]>(
                (acc, m, i) => (m.isSameOrBefore(timeFrom) ? [...acc, i] : acc),
                []
              )}
              onChange={(value, isFrom) => {
                if (isFrom) {
                  const result = getFormattedTimeWithAddedMinutes({
                    timeFrom: value,
                    timeTo: hook.bookingWorkdayTimeTo,
                  })

                  updateCompanySetting(
                    omitByNull({
                      bookingWorkdayTimeFrom: result.from,
                      bookingWorkdayTimeTo: result.to,
                    })
                  )
                } else {
                  updateCompanySetting({
                    bookingWorkdayTimeTo: moment(value, 'h:mm a').format('HH:mm'),
                  })
                }
              }}
            />
          </div>
          <div className="mb-4">
            <LocationWorkingHours workingHoursGroupsHook={workingHoursGroupsHook} />
          </div>
          <div className={cx('bg-grey-100 ', css.bottom_divider)}>
            <div className="p-2">
              <Button
                minimal
                blue
                iconName="plus"
                text="ADD NEW LOCATION GROUP"
                onClick={workingHoursGroupsHook.addNewWorkingHoursGroup}
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      name: 'Email Notifications',
      description: 'Receive booking email notifications',
      action: (
        <Switch
          large
          innerLabelChecked="ON"
          innerLabel="OFF"
          checked={emailNotificationEnabled}
          onChange={() => {
            setEmailNotificationEnabled((value) => {
              updateCompanySetting({
                bookingReminderEnabled: !value,
                bookingConfirmationEnabled: !value,
                bookingCancellationEnabled: !value,
                bookingConfirmationOnbehalfEnabled: false,
                bookingCancellationOnbehalfEnabled: false,
                bookingCheckinReminderOnbehalfEnabled: false,
              })
              return !value
            })
          }}
        />
      ),
      attachedSettings: emailNotificationEnabled && (
        <div className="bg-grey-100 rounded pl-5 py-2 ">
          <div className={css.onBehalfLabel}>Send email alerts for bookings that are:</div>
          <div className="row mb-4">
            <Checkbox
              checked={hook.bookingReminderEnabled}
              onChange={() => {
                updateCompanySetting({
                  bookingReminderEnabled: !hook.bookingReminderEnabled,
                })
              }}>
              Receive booking reminder email notifications 24hrs before booking start time
            </Checkbox>
          </div>
          <div className="row mb-4">
            <Checkbox
              checked={hook.bookingConfirmationEnabled}
              onChange={() => {
                updateCompanySetting({
                  bookingConfirmationEnabled: !hook.bookingConfirmationEnabled,
                })
              }}>
              Receive booking confirmation emails
            </Checkbox>
          </div>
          <div className="row mb-4">
            <Checkbox
              checked={hook.bookingCancellationEnabled}
              onChange={() => {
                updateCompanySetting({
                  bookingCancellationEnabled: !hook.bookingCancellationEnabled,
                })
              }}>
              Receive booking cancellation emails
            </Checkbox>
          </div>
          <div className={css.onBehalfLabel}>
            When booking on behalf of someone else, send email notifications for:
          </div>

          <div className="row mb-4">
            <Checkbox
              checked={hook.bookingConfirmationOnbehalfEnabled}
              onChange={() => {
                updateCompanySetting({
                  bookingConfirmationOnbehalfEnabled: !hook.bookingConfirmationOnbehalfEnabled,
                })
              }}>
              Confirmation of Bookings.
            </Checkbox>
          </div>
          <div className="row mb-4">
            <Checkbox
              checked={hook.bookingCheckinReminderOnbehalfEnabled}
              onChange={() => {
                updateCompanySetting({
                  bookingCheckinReminderOnbehalfEnabled:
                    !hook.bookingCheckinReminderOnbehalfEnabled,
                })
              }}>
              Employee Check-in.
            </Checkbox>
          </div>
          <div className="row mb-4">
            <Checkbox
              checked={hook.bookingCancellationOnbehalfEnabled}
              onChange={() => {
                updateCompanySetting({
                  bookingCancellationOnbehalfEnabled: !hook.bookingCancellationOnbehalfEnabled,
                })
              }}>
              Booking cancellations.
            </Checkbox>
          </div>
        </div>
      ),
    },
    {
      name: 'QR Codes',
      permitted: [Role.ADMIN, Role.POWER_ADMIN],
      feature: features.includes('qrCode'),
      description:
        'Generate QR codes for desks so employees can check in via QR code. QR codes are managed and downloaded from the Buildings and Floors Management module.',
      attachedSettings: (
        <div style={{ marginBottom: 16 }} className="flex">
          <div
            className="text-grey-700"
            style={{
              marginRight: 20,
              marginBottom: 10,
              width: 550,
              flexGrow: 1,
            }}>
            <div className="flex">
              <span style={{ whiteSpace: 'pre' }}>ON – </span>
              <span>
                enable the generation of QR codes; QR code options will display on the Buildings and
                Floors Management module and directly from desk information drawers.
              </span>
            </div>
            <p>
              OFF - QR codes will not be available, and QR code options will not appear on Eptura
              Web app.
            </p>
          </div>
        </div>
      ),
      action: (
        <Switch
          large
          innerLabelChecked="ON"
          innerLabel="OFF"
          checked={hook.qrCodeEnabled}
          onChange={() => {
            const enabledQRCode = hook.qrCodeEnabled
            updateCompanySetting({
              qrCodeEnabled: !hook.qrCodeEnabled,
            })
            track.qrCodeSetting({
              eventName: Event.QR_CODE_SETTINGS_CLICK,
              props: {
                component: 'Setting',
                actionName: `QR Code Setting ${enabledQRCode ? 'disabled' : 'enabled'}`,
              },
            })
          }}
        />
      ),
    },
  ].filter(
    (category) =>
      category.name !== 'Booking Alias Email' || treatments[featureFlagName]?.treatment === 'on'
  )

  const applicationSettings: Setting[] = [
    {
      name: 'Import Notification',
      description: 'Select to recieve email notification',
      action: (
        <Switch
          large
          innerLabelChecked="ON"
          innerLabel="OFF"
          checked={hook.employeeImportNotificationEnabled}
          onChange={() =>
            updateCompanySetting({
              employeeImportNotificationEnabled: !hook.employeeImportNotificationEnabled,
            })
          }
        />
      ),
    },
    {
      name: 'Enforce Two-Factor Authentication',
      description:
        'Select to enforce two-factor authentication on all of the users in your organization.',
      permitted: [Role.ADMIN],
      action: (
        <Switch
          large
          innerLabelChecked="ON"
          innerLabel="OFF"
          checked={hook.enforceAuthAppEnabled}
          onChange={() =>
            updateCompanySetting({
              enforceAuthAppEnabled: !hook.enforceAuthAppEnabled,
            })
          }
        />
      ),
      attachedSettings: (
        <ol style={{ listStyle: 'inside' }}>
          <li>
            To activate Two-Factor Authentication please logout from the app. (We will send
            two-factor one time passcode to your email address)
          </li>
          <li>
            Go to your mail box and find the email from no-reply@spaceiq.com with subject “Your
            Eptura one-time passcode”
          </li>
          <li>Use one time passcode to enter when you sign in.</li>
        </ol>
      ),
    },
  ]

  const bookingSettings: Setting[] = [
    {
      name: 'Booking Type',
      description: 'Select which booking types to permit your employees',
      action: (
        <div
          className="text-primary hover:underline cursor-pointer mt-2 mb-3"
          onClick={() =>
            setShownPostDescriptionName(
              shownPostDescriptionName === 'Booking Type' ? '' : 'Booking Type'
            )
          }>
          {shownPostDescriptionName === 'Booking Type' ? 'CLOSE' : 'CHANGE'}
        </div>
      ),
      state: getCurrentBookingType({
        dailyBookingEnabled: hook.dailyBookingEnabled,
        hourlyBookingEnabled: hook.hourlyBookingEnabled,
      }),
      postDescription: (
        <div>
          <RadioGroup
            selectedValue={getCurrentBookingType({
              dailyBookingEnabled: hook.dailyBookingEnabled,
              hourlyBookingEnabled: hook.hourlyBookingEnabled,
            })}
            onChange={({ currentTarget: { value } }: { currentTarget: { value: unknown } }) => {
              switch (value as BookingTypes) {
                case BookingTypes.BOTH:
                  updateCompanySetting({
                    hourlyBookingEnabled: true,
                    dailyBookingEnabled: true,
                  })
                  break
                case BookingTypes.ALL_DAY_ONLY:
                  updateCompanySetting({
                    hourlyBookingEnabled: false,
                    dailyBookingEnabled: true,
                  })
                  break
                case BookingTypes.HOURLY_ONLY:
                  updateCompanySetting({
                    hourlyBookingEnabled: true,
                    dailyBookingEnabled: false,
                  })
                  break
                default:
                // Do nothing
              }
            }}>
            <Radio
              label={BookingTypes.BOTH}
              value={BookingTypes.BOTH}
              style={{ marginBottom: 'none' }}
            />
            <div className="text-grey-600 mb-4" style={{ marginLeft: 26 }}>
              Select to allow both Hourly & All Day Booking
            </div>
            <Radio
              label={BookingTypes.HOURLY_ONLY}
              value={BookingTypes.HOURLY_ONLY}
              style={{ marginBottom: 'none' }}
            />
            <div className="text-grey-600 mb-4" style={{ marginLeft: 26 }}>
              Select to disable the All Day Toggle on Booking Calendars
            </div>

            <Radio
              label={BookingTypes.ALL_DAY_ONLY}
              value={BookingTypes.ALL_DAY_ONLY}
              style={{ marginBottom: 'none' }}
            />

            <div className="text-grey-600 mb-4" style={{ marginLeft: 26 }}>
              Select to disable the time selectors on Booking Calendars
            </div>
          </RadioGroup>
          <div className="text-grey-600">
            * Only the Booking selection components are affected, not the import or external API
            requests
          </div>
        </div>
      ),
    },
    {
      name: 'Booking Restriction',
      description: 'Select which restrictions are applied to desks',
      action: (
        <div
          className="text-primary hover:underline cursor-pointer mt-2 mb-3"
          onClick={() =>
            setShownPostDescriptionName(
              shownPostDescriptionName === 'Booking Restriction' ? '' : 'Booking Restriction'
            )
          }>
          {shownPostDescriptionName === 'Booking Restriction' ? 'CLOSE' : 'CHANGE'}
        </div>
      ),
      state: getCurrentBookingRestriction({
        hotelingDeskDeptEnforcementEnabled: hook.hotelingDeskDeptEnforcementEnabled,
        hotelingDeskWpgEnforcementEnabled: hook.hotelingDeskWpgEnforcementEnabled,
        hotelingDeskWpgEnforcementOperator: hook.hotelingDeskWpgEnforcementOperator,
      }),
      postDescription: (
        <div>
          <div className="flex flex-col">
            <div className="flex my-2">
              <div className="flex flex-col flex-grow">
                <div className="text-lg leading-normal font-medium mb-3">Department</div>
                <div className="text-sm text-grey-700">
                  Select to restrict desk booking to allocated department
                </div>
              </div>
              <div className="flex flex-col">
                <Switch
                  large
                  innerLabelChecked="ON"
                  innerLabel="OFF"
                  checked={hook.hotelingDeskDeptEnforcementEnabled}
                  onChange={() => {
                    const turnOffoverrideBookingRestriction =
                      hook.hotelingDeskDeptEnforcementEnabled &&
                      !hook.hotelingDeskWpgEnforcementEnabled
                        ? false
                        : hook.overrideBookingRestrictionEnabled
                    const turnOffoverrideSpaceTypeBookingRestriction =
                      hook.hotelingDeskDeptEnforcementEnabled &&
                      !hook.hotelingDeskWpgEnforcementEnabled
                        ? false
                        : hook.overrideSpaceTypeBookingRestrictionEnabled
                    updateCompanySetting({
                      hotelingDeskDeptEnforcementEnabled: !hook.hotelingDeskDeptEnforcementEnabled,
                      overrideBookingRestrictionEnabled: turnOffoverrideBookingRestriction,
                      overrideSpaceTypeBookingRestrictionEnabled:
                        turnOffoverrideSpaceTypeBookingRestriction,
                    })
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col">
            <div className="flex my-2">
              <div className="flex flex-col flex-grow">
                <div className="text-lg leading-normal font-medium mb-3">Workplace Group</div>
                <div className="text-sm text-grey-700">
                  Select to restrict desk booking to allocated WPG
                </div>
              </div>

              <div className="flex flex-col">
                <Switch
                  large
                  innerLabelChecked="ON"
                  innerLabel="OFF"
                  checked={hook.hotelingDeskWpgEnforcementEnabled}
                  onChange={() => {
                    const turnOffoverrideBookingRestriction =
                      hook.hotelingDeskWpgEnforcementEnabled &&
                      !hook.hotelingDeskDeptEnforcementEnabled
                        ? false
                        : hook.overrideBookingRestrictionEnabled
                    const turnOffoverrideSpaceTypeBookingRestriction =
                      hook.hotelingDeskWpgEnforcementEnabled &&
                      !hook.hotelingDeskDeptEnforcementEnabled
                        ? false
                        : hook.overrideSpaceTypeBookingRestrictionEnabled
                    updateCompanySetting({
                      hotelingDeskWpgEnforcementEnabled: !hook.hotelingDeskWpgEnforcementEnabled,
                      overrideBookingRestrictionEnabled: turnOffoverrideBookingRestriction,
                      overrideSpaceTypeBookingRestrictionEnabled:
                        turnOffoverrideSpaceTypeBookingRestriction,
                    })
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col">
            <div className="flex my-2">
              <div className="flex flex-col flex-grow">
                <div className="text-lg leading-normal font-medium mb-3">
                  Dept/Workplace Group Interaction
                </div>
                <div className="text-sm text-grey-700 whitespace-pre-line">
                  {`Select 'AND' to restrict desk booking to allocated Dept and WPG

                    Select 'OR' to restrict desk booking to allocated Dept or WPG

                    * Only active if both above settings are ON`}
                </div>
              </div>

              <div className="flex flex-col">
                <ControlGroup className="mt-1">
                  <Button
                    disabled={
                      !(
                        hook.hotelingDeskWpgEnforcementEnabled &&
                        hook.hotelingDeskDeptEnforcementEnabled
                      )
                    }
                    style={{ borderRadius: '4px 0 0 4px' }}
                    onClick={() =>
                      updateCompanySetting({
                        hotelingDeskWpgEnforcementOperator: 'AND',
                      })
                    }
                    intent={
                      hook.hotelingDeskWpgEnforcementOperator === 'AND'
                        ? Intent.PRIMARY
                        : Intent.NONE
                    }
                    text="AND"
                  />
                  <Button
                    disabled={
                      !(
                        hook.hotelingDeskWpgEnforcementEnabled &&
                        hook.hotelingDeskDeptEnforcementEnabled
                      )
                    }
                    style={{ borderRadius: '0 4px 4px 0' }}
                    onClick={() =>
                      updateCompanySetting({
                        hotelingDeskWpgEnforcementOperator: 'OR',
                      })
                    }
                    intent={
                      hook.hotelingDeskWpgEnforcementOperator === 'OR'
                        ? Intent.PRIMARY
                        : Intent.NONE
                    }
                    text="OR"
                  />
                </ControlGroup>
              </div>
            </div>
          </div>

          <div className="flex flex-col">
            <div className="flex my-2">
              <div className="flex flex-col flex-grow">
                <div className="text-lg leading-normal font-medium mb-3">Allow restrictions</div>
                <div className="text-sm text-grey-700">
                  <p>Select so that these users can't override restrictions:</p>
                  <ul className={css.adminList}>
                    <li>Booking manager</li>
                    <li>Department admin</li>
                    <li>Location admin</li>
                    <li>Space captain</li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col">
                <Switch
                  disabled={
                    !(
                      hook.hotelingDeskWpgEnforcementEnabled ||
                      hook.hotelingDeskDeptEnforcementEnabled
                    )
                  }
                  large
                  innerLabelChecked="ON"
                  innerLabel="OFF"
                  checked={hook.overrideBookingRestrictionEnabled}
                  onChange={() => {
                    updateCompanySetting({
                      overrideBookingRestrictionEnabled: !hook.overrideBookingRestrictionEnabled,
                    })
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col">
            <div className="flex my-2">
              <div className="flex flex-col flex-grow">
                <div className="text-lg leading-normal font-medium mb-3">
                  Override space type restrictions
                </div>
                <div className="text-sm text-grey-700">
                  <p>
                    Select to enable employees to book personal spaces with existing Workplace
                    Group/Department restrictions:
                  </p>
                </div>
              </div>
              <div className="flex flex-col">
                <Switch
                  large
                  innerLabelChecked="ON"
                  innerLabel="OFF"
                  disabled={
                    !(
                      hook.hotelingDeskWpgEnforcementEnabled ||
                      hook.hotelingDeskDeptEnforcementEnabled
                    )
                  }
                  checked={hook.overrideSpaceTypeBookingRestrictionEnabled}
                  onChange={() => {
                    const updateObj = {
                      overrideSpaceTypeBookingRestrictionEnabled:
                        !hook.overrideSpaceTypeBookingRestrictionEnabled,
                    }
                    updateCompanySetting({ ...updateObj })
                    if (!hook.overrideSpaceTypeBookingRestrictionEnabled) {
                      updateCompanySetting({
                        ...updateObj,
                        overriddenBookingRestrictionSpaceTypes: [],
                      })
                    }
                  }}
                />
              </div>
            </div>
          </div>

          {hook.overrideSpaceTypeBookingRestrictionEnabled && (
            <div className="wrapper padded h-400">
              <SelectWrapper>
                <MultiSpaceTypeSelect
                  title=""
                  buttonText="Select space types"
                  shouldIncludeSelectAll={true}
                  seatableSpaceTypesOnly={true}
                  values={(fetchRestrictedSpaceTypes() as unknown as ISpaceType[]) || []}
                  onChange={(spaceTypes: ISpaceType[]) => {
                    updateCompanySetting({
                      overriddenBookingRestrictionSpaceTypes: spaceTypes.map(
                        (spaceType: ISpaceType) => spaceType?.name || spaceType
                      ),
                    })
                  }}
                />
              </SelectWrapper>
            </div>
          )}
        </div>
      ),
    },
    {
      name: 'Booking Desk Usage Types',
      description: 'Choose what types of desks you’ll allow employees to book.',
      action: (
        <div
          className="text-primary hover:underline cursor-pointer mt-2 mb-3"
          onClick={() =>
            setShownPostDescriptionName(
              shownPostDescriptionName === 'Booking Desk Usage Types'
                ? ''
                : 'Booking Desk Usage Types'
            )
          }>
          {shownPostDescriptionName === 'Booking Desk Usage Types' ? 'CLOSE' : 'CHANGE'}
        </div>
      ),
      state:
        hook.bookableSpaceTypes &&
        hook.bookableSpaceTypes.map((e) => e.split('_').map(capitalizeStr).join(' ')).join(', '),
      postDescription: (
        <div className="bg-grey-100 rounded pl-5 py-2 ">
          <div className="row">
            <BookableDeskEdit
              bookableDesks={bookableDesks}
              requiredDesks={requiredDesks}
              hasConfirmBox={true}
              bookableSpaceTypes={hook.bookableSpaceTypes}
              onChange={(bookableSpaceTypes) => {
                updateCompanySetting({ bookableSpaceTypes })
              }}
            />
          </div>
          <LocationBookableDesk
            requiredDesks={requiredDesks}
            bookableDesks={bookableDesks}
            bookingLocationEnabled={hook.bookableSpaceTypesLocationsOverridesEnabled}
            toggleLocation={() => {
              updateCompanySetting({
                bookableSpaceTypesLocationsOverridesEnabled:
                  !hook.bookableSpaceTypesLocationsOverridesEnabled,
              })
            }}
          />
        </div>
      ),
    },
    {
      name: 'Cap Future Bookings',
      description: 'Limit how far into the future bookings can be made.',
      permitted: [Role.ADMIN, Role.POWER_ADMIN],
      attachedSettings: true && (
        <TimeCountSelect
          value={
            omit(hook.maxFutureBookingDuration, '__typename') as {
              [type: string]: number
            }
          }
          onChange={(data) => {
            updateCompanySetting({
              maxFutureBookingDuration: data,
            })
          }}
          maximums={{ days: 180, weeks: 26, months: 6 }}
        />
      ),
    },
    {
      name: 'Booking Capacity',
      permitted: [Role.ADMIN, Role.POWER_ADMIN],
      description: 'Restrict Booking Capacity per floor for all locations',
      action: (
        <Switch
          large
          innerLabelChecked="ON"
          innerLabel="OFF"
          checked={hook.enableBookingCapacityCap}
          onChange={() =>
            updateCompanySetting({
              enableBookingCapacityCap: !hook.enableBookingCapacityCap,
            })
          }
        />
      ),
      attachedSettings: hook.enableBookingCapacityCap && (
        <>
          <div style={{ marginBottom: 16 }} className="flex">
            <div
              className="text-grey-700"
              style={{
                marginRight: 20,
                marginBottom: 10,
                width: 550,
                flexGrow: 1,
              }}>
              <div className="text-lg leading-normal text-grey-700 font-medium mb-2">
                Set up default booking capacity, where:
              </div>
              <div>0% - booking unavailable</div>
              <div>100% - booking available for the entire floor</div>
            </div>
            <div className="flex items-start" style={{ width: 302.25 }}>
              <PercentSelect
                value={hook.companyBookingCapacityCap}
                onChange={(opt) => updateCompanySetting({ companyBookingCapacityCap: opt.value })}
              />
            </div>
          </div>
          <div className="flex flex-col">
            <div className="flex">
              <div className="flex flex-col flex-grow ">
                <div className="text-lg leading-normal text-grey-700 font-medium mb-2">
                  Specify Restrictions per Location
                </div>
                <div className="text-sm text-grey-700">
                  Restrict booking capacity per floor in different locations/cities
                </div>
              </div>
              <div className="flex flex-col items-end justify-between" style={{ minWidth: 120 }}>
                <Switch
                  large
                  innerLabelChecked="ON"
                  innerLabel="OFF"
                  checked={hook.enableLocationBookingCapacityCap}
                  onChange={() =>
                    updateCompanySetting({
                      enableLocationBookingCapacityCap: !hook.enableLocationBookingCapacityCap,
                    })
                  }
                />
              </div>
            </div>
            {hook.enableLocationBookingCapacityCap && <LocationCapacities />}
          </div>
        </>
      ),
    },
    {
      name: 'Single Active Reservation Booking',
      description: 'Limit desk reservation to one desk per time period.',
      action: (
        <Switch
          large
          innerLabelChecked="ON"
          innerLabel="OFF"
          checked={hook.hotelingBookingOverlapRestrictionEnabled}
          onChange={() =>
            updateCompanySetting({
              hotelingBookingOverlapRestrictionEnabled:
                !hook.hotelingBookingOverlapRestrictionEnabled,
            })
          }
        />
      ),
    },
    {
      name: 'Enable Recurring Bookings',
      description: 'Allow Bookings to repeat.',
      action: (
        <Switch
          large
          innerLabelChecked="ON"
          innerLabel="OFF"
          checked={hook.enableRecurringBookings}
          onChange={() =>
            updateCompanySetting({
              enableRecurringBookings: !hook.enableRecurringBookings,
            })
          }
        />
      ),
    },
    {
      name: '​Enable Employee Bookings Search',
      description: `When you search for an employee,
        results include a link to a list of the employee's reservations`,
      action: (
        <Switch
          large
          innerLabelChecked="ON"
          innerLabel="OFF"
          checked={hook.viewPeerCurrentBookings}
          onChange={() =>
            updateCompanySetting({
              viewPeerCurrentBookings: !hook.viewPeerCurrentBookings,
            })
          }
        />
      ),
    },
    {
      name: 'Show names on reserved desks (Mobile App)',
      description: 'Display names of employees next to their desk bookings.',
      action: (
        <Switch
          large
          innerLabelChecked="ON"
          innerLabel="OFF"
          checked={hook.showOtherBookers}
          onChange={() =>
            updateCompanySetting({
              showOtherBookers: !hook.showOtherBookers,
            })
          }
        />
      ),
    },
    {
      name: 'Require Check-in',
      description: 'Select to require a check-in on desk booking.',
      action: (
        <Switch
          large
          innerLabelChecked="ON"
          innerLabel="OFF"
          checked={hook.bookingCheckInRequired}
          onChange={() =>
            updateCompanySetting({
              bookingCheckInRequired: !hook.bookingCheckInRequired,
            })
          }
        />
      ),
      attachedSettings: hook.bookingCheckInRequired && (
        <>
          <div className="flex flex-col">
            <div className="flex">
              <div className="flex flex-col flex-grow">
                <div className="text-lg leading-normal font-medium mb-3">Check In Time</div>
              </div>
            </div>
            <div className="flex items-center mx-2 mb-2">
              <HoursSelect
                onChange={({ value }) => {
                  if (
                    features.includes('healthCheckAcknowledgment') &&
                    hook.enableHealthScreening &&
                    hook.hoursHealthMessageSentBeforeBooking < value
                  ) {
                    Toaster.fail({
                      message:
                        'Check In time has to be less than hours before health check confirmation',
                    })
                  } else {
                    updateCompanySetting({
                      hoursBeforeBookingCheckin: value,
                    })
                  }
                }}
                value={hook.hoursBeforeBookingCheckin || 2}
                limit={24}
                disableSearch={true}
              />
              <div className="flex text-sm text-grey-700 items-center ml-2 ">
                before booking start time
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            <div className="flex">
              <div className="flex flex-col flex-grow">
                <div className="text-lg leading-normal font-medium mt-1 mb-3">
                  Automatically Release Booked Desks
                </div>
              </div>

              <div className="flex flex-col">
                <Switch
                  large
                  innerLabelChecked="ON"
                  innerLabel="OFF"
                  checked={hook.bookingAutoReleaseEnabled}
                  onChange={() =>
                    updateCompanySetting({
                      bookingAutoReleaseEnabled: !hook.bookingAutoReleaseEnabled,
                    })
                  }
                />
              </div>
            </div>
            <div className="flex items-center mx-2 mb-2">
              <HoursSelect
                onChange={({ value }) =>
                  updateCompanySetting({
                    autoReleaseGracePeriod: value,
                  })
                }
                value={hook.autoReleaseGracePeriod}
                limit={12}
                disableSearch={true}
              />
              <div className="flex text-sm text-grey-700 items-center ml-2 ">
                after booking start time, cancel the reservation if there is no check-in.
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            <div className="flex my-2">
              <div className="flex flex-col flex-grow">
                <div className="text-lg leading-normal font-medium mb-3">Exempt From Check-in</div>
                <div className="text-sm text-grey-700 mb-3">
                  Mark certain employees as exempt from needing to check-in.
                </div>
                {hook.enableHealthScreening ? (
                  <b>
                    All employees are required to check in because health screenings are enabled.
                  </b>
                ) : (
                  <MultiEmployeeSelect
                    noTitle
                    title={
                      hook.enableHealthScreening
                        ? 'Currently disabled when health check is enabled'
                        : ''
                    }
                    disabled={hook.enableHealthScreening}
                    values={exemptedEmployees}
                    onChange={(items, param) => {
                      if (param.status === 'add') {
                        api.employee
                          .update({
                            exemptFromCheckIn: true,
                            id: param.id,
                          })
                          .then(({ id, name, email, assets }) =>
                            setExemptedEmployees([
                              ...exemptedEmployees,
                              { id, name, email, assets },
                            ])
                          )
                      } else {
                        api.employee
                          .update({
                            exemptFromCheckIn: false,
                            id: param.id,
                          })
                          .then(() =>
                            setExemptedEmployees([
                              ...exemptedEmployees.filter((x) => x.id !== param.id),
                            ])
                          )
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            <div className="flex my-2">
              <div className="flex flex-col flex-grow">
                <div className="text-lg leading-normal font-medium mb-3">Cancel Bookings</div>
                <div className="text-sm text-grey-700">
                  Allow Cancellation of booking after checkin
                </div>
              </div>

              <div className="flex flex-col">
                <Switch
                  large
                  innerLabelChecked="ON"
                  innerLabel="OFF"
                  checked={hook.bookingCheckInCancelEnabled}
                  onChange={() =>
                    updateCompanySetting({
                      bookingCheckInCancelEnabled: !hook.bookingCheckInCancelEnabled,
                    })
                  }
                />
              </div>
            </div>
          </div>
        </>
      ),
    },
    {
      name: 'Health Screening',
      description: 'Enable or disable health checks; customize health checks company-wide.',
      feature: features.includes('healthCheckAcknowledgment'),
      action: (
        <Switch
          large
          innerLabelChecked="ON"
          innerLabel="OFF"
          checked={hook.enableHealthScreening}
          onChange={() => {
            if (!hook.enableHealthScreening && exemptedEmployees.length > 0) {
              setShowConfirmBox(true)
            } else {
              updateHealthConfirm(true)
            }
          }}
        />
      ),
      attachedSettings: hook.enableHealthScreening && (
        <>
          <div>
            <div>
              <div className="text-lg leading-normal font-medium mb-3">Employee Notifications</div>
              <div className="flex items-center m-2">
                <HoursSelect
                  min={1}
                  onChange={({ value }) => {
                    if (hook.hoursBeforeBookingCheckin > value) {
                      Toaster.fail({
                        message:
                          'Hours for Health check has to be greater than or equal to check in time',
                      })
                    } else {
                      updateCompanySetting({
                        hoursHealthMessageSentBeforeBooking: value,
                      })
                    }
                  }}
                  value={hook.hoursHealthMessageSentBeforeBooking}
                />
                <div className="flex text-sm text-grey-700 items-center ml-2 ">
                  before booking time, health check becomes available, and notification is sent.
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex my-2">
                  <div className="flex flex-col flex-grow">
                    <div className="text-lg leading-normal font-medium mb-3">
                      Admin Notifications
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <Switch
                      large
                      innerLabelChecked="ON"
                      innerLabel="OFF"
                      checked={hook.healthNoticeEnabled}
                      onChange={() =>
                        updateCompanySetting({
                          healthNoticeEnabled: !hook.healthNoticeEnabled,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="text-sm text-grey-700 mb-3">
                  <div className="mb-2" style={{ maxWidth: 600 }}>
                    ON – email alerts are sent to admins when a health check isn't completed; each
                    admin can enable or disable this alert individually in User Settings.{' '}
                  </div>
                  <div>OFF - admin alerts are disabled</div>
                </div>
              </div>
              <div className="flex items-center mb-4">
                <HoursSelect
                  onChange={({ value }) =>
                    updateCompanySetting({
                      hoursAdminsGetHealthNoticeAfterEmployees: value,
                    })
                  }
                  value={hook.hoursAdminsGetHealthNoticeAfterEmployees}
                />
                <div className="flex text-sm text-grey-700 items-center ml-2">
                  after health check is available, send admin alerts when a health check isn't
                  completed
                </div>
              </div>
            </div>
            <div className="flex">
              <div className="mr-3">
                <div>Company-wide health check message</div>
                <TextArea
                  style={{ width: 500 }}
                  growVertically={true}
                  large={true}
                  onChange={({ target: { value } }) => setHealthScreenMessage(value)}
                  value={healthScreenMessage}
                  disabled={!isUpdatingScreening}
                />
              </div>
              <div>
                <div>Confirmation Button Text</div>
                <InputGroup
                  style={{ width: 300 }}
                  onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
                    setHealthConfirmationMessage(value)
                  }
                  value={healthConfirmationMessage}
                  disabled={!isUpdatingScreening}
                />
              </div>
            </div>
            <div className="flex justify-end">
              {isUpdatingScreening ? (
                <div>
                  <Button
                    className="mr-3"
                    minimal
                    text="Cancel"
                    iconName="times"
                    onClick={() => {
                      setIsUpdatingScreening(false)
                      setHealthScreenMessage(hook.healthScreeningMessage)
                      setHealthConfirmationMessage(hook.healthScreeningConfirmButtonText)
                    }}
                  />
                  <Button
                    minimal
                    text="Save"
                    iconName="check"
                    intent="primary"
                    loading={isSavingScreening}
                    onClick={() => {
                      setIsSavingScreening(true)
                      updateCompanySetting({
                        healthScreeningConfirmButtonText: healthConfirmationMessage,
                        healthScreeningMessage: healthScreenMessage,
                      })
                        .then(() => setIsSavingScreening(false))
                        .catch((error) => {
                          setIsSavingScreening(false)
                          Toaster.fail({ error })
                        })
                    }}
                  />
                </div>
              ) : (
                <Button
                  className="mr-3"
                  minimal
                  blue
                  text="Edit"
                  iconName="pencil"
                  onClick={() => {
                    setIsUpdatingScreening(true)
                  }}
                />
              )}
            </div>
          </div>
          <Divider />

          <LocationHealthChecks
            disabledHealthCheckLocations={hook.locationHealthCheckDisabled}
            updateDisabled={(locs) =>
              updateCompanySetting({
                locationHealthCheckDisabledIds: locs.map(({ id }) => id),
              }).then(() => hook.reload())
            }
          />
        </>
      ),
    },
    {
      name: 'Enable notes',
      description: 'Allow Notes for Bookings.',
      action: (
        <Switch
          large
          innerLabelChecked="ON"
          innerLabel="OFF"
          checked={hook.bookingNotesEnabled}
          onChange={() =>
            updateCompanySetting({
              bookingNotesEnabled: !hook.bookingNotesEnabled,
            })
          }
        />
      ),
    },
  ]

  if (!hook.bookingCheckInRequired) {
    let indx = userSettings.findIndex((e) => e.name === 'Health Check Notifications')
    if (indx > -1) {
      userSettings.splice(indx, 1)
    }
    indx = -1
    indx = bookingSettings.findIndex((e) => e.name === 'Health Screening')
    if (indx > -1) {
      bookingSettings.splice(indx, 1)
    }
  }

  if (legacyMode) {
    const index = userSettings.findIndex((e) => e.name === 'Locations')
    if (index > -1) {
      userSettings.splice(index, 1)
    }
  }

  const customizationSettings: Setting[] = [
    {
      name: 'Building Types',
      description: 'Manage Company Building Types',
      action: (
        <div
          className="text-primary hover:underline cursor-pointer "
          style={{ minWidth: 100 }}
          onClick={() =>
            legacyMode
              ? navigateToAphrodite('/settings/buildingTypes')
              : navigate?.('/buildingTypeManagement')
          }>
          READ MORE
        </div>
      ),
    },
    {
      name: 'Space Types',
      description: 'Manage Company Space Types',
      action: (
        <div
          className="text-primary hover:underline cursor-pointer "
          style={{ minWidth: 100 }}
          onClick={() =>
            legacyMode
              ? navigateToAphrodite('/settings/spaceTypes')
              : navigate?.('/spaceTypeManagement')
          }>
          READ MORE
        </div>
      ),
    },
  ]

  const integrationSettings: Setting[] = [
    {
      name: 'Third Party Integrations',
      description: 'Integrate Eptura with third party APIs',
      action: (
        <div
          className="text-primary hover:underline cursor-pointer "
          style={{ minWidth: 100 }}
          onClick={() =>
            legacyMode ? navigateToAphrodite('settings/integrations') : navigate?.('/integrations')
          }>
          READ MORE
        </div>
      ),
    },
    {
      name: 'API Documentation',
      description: 'Learn about the Eptura APIs',
      action: (
        <a
          className="text-primary hover:underline cursor-pointer "
          style={{ minWidth: 100 }}
          target="_blank"
          rel="noopener noreferrer"
          /* eslint-disable @typescript-eslint/ban-ts-comment */
          // @ts-ignore developerDocUrl is a custom attr we add on aphrodite
          href={window.developerDocUrl ?? 'https://developer.spaceiq.com'}>
          READ MORE
        </a>
      ),
    },
  ]

  const categories: SettingCategory[] = [
    {
      name: 'User Settings',
      leftIcon: 'user',
      id: 'user',
      permitted: [
        Role.ADMIN,
        Role.POWER_ADMIN,
        Role.DEPARTMENT_ADMIN,
        Role.IT,
        Role.LOCATION_ADMIN,
        Role.SPACE_CAPTAIN,
        Role.VIEWER,
        Role.BOOKINGS_MANAGER,
      ],
      settings: userSettings,
    },
    {
      name: 'Company Settings',
      leftIcon: 'building',
      id: 'company',
      permitted: [Role.ADMIN, Role.POWER_ADMIN],
      settings: companySettings,
    },
    {
      name: 'Application Settings',
      leftIcon: 'cog',
      id: 'application',
      permitted: [Role.ADMIN, Role.POWER_ADMIN],
      settings: applicationSettings,
    },
    {
      name: 'Desk Booking Settings',
      leftIcon: 'desktop',
      id: 'booking',
      permitted: [Role.ADMIN, Role.POWER_ADMIN],
      settings: bookingSettings,
    },
    {
      name: 'Company Customizations',
      leftIcon: 'edit',
      id: 'customizations',
      permitted: [Role.ADMIN],
      settings: customizationSettings,
    },
    {
      name: 'Integrations',
      leftIcon: 'puzzle-piece',
      id: 'integrations',
      permitted: [Role.ADMIN, Role.IT],
      settings: legacyMode ? integrationSettings : integrationSettings.splice(1, 1),
    },
  ]

  const updateHealthConfirm = (bool = false) => {
    const param: Partial<{ [key in keyof ISettings]: unknown }> = {
      enableHealthScreening: !hook.enableHealthScreening,
    }
    if (bool && hook.hoursHealthMessageSentBeforeBooking <= hook.hoursBeforeBookingCheckin) {
      const indx = timeList.indexOf(hook.hoursBeforeBookingCheckin)
      param.hoursHealthMessageSentBeforeBooking = timeList[indx + 1]
    }
    updateCompanySetting(param)
  }
  const confirm = () => {
    Promise.all(
      exemptedEmployees.map(
        (e) =>
          new Promise((resolve) => {
            api.employee
              .update({
                exemptFromCheckIn: false,
                id: e.id,
              })
              .then(({ id }) => resolve(id))
          })
      )
    ).then((removedIds) => {
      setExemptedEmployees(
        exemptedEmployees.filter(({ id }) => !(removedIds as unknown as string[]).includes(id))
      )
      updateHealthConfirm()
    })
  }

  const close = () => setShowConfirmBox(false)

  return (
    <>
      <ConfirmBox
        isOpen={showConfirmBox}
        confirm={confirm}
        close={close}
        title="Health Acknowledgement Change"
        text="Enabling Health Check will clear the employee exemption list. Do you wish to continue?"
      />
      <div className="h-4 max-w-full" />
      <div className="flex relative">
        {legacyMode && (
          <div className="w-64 sticky" style={{ top: 74 }}>
            {categories
              .filter((c) => c.permitted.includes(userRole))
              .map((category, i) => (
                <Category
                  key={i}
                  {...category}
                  isActive={activeCat === category.id}
                  onClick={setActiveCat}
                />
              ))}
          </div>
        )}

        <div className="flex flex-grow">
          <div className="ml-8" style={{ width: '100%' }}>
            {categories
              .find(({ id }) => id === activeCat)
              ?.settings.map((setting, i) =>
                (setting.permitted === undefined || setting.permitted.includes(userRole)) &&
                (setting.feature === undefined || setting.feature) &&
                (setting.superUserOnly === undefined ||
                  (setting.superUserOnly && user?.isSuperUser)) ? (
                  <Setting
                    key={i}
                    {...setting}
                    shownPostDescriptionName={shownPostDescriptionName}
                  />
                ) : null
              )}
          </div>
        </div>
      </div>
    </>
  )
}

function Category({
  name,
  leftIcon,
  isActive,
  id,
  onClick,
}: SettingCategory & { isActive: boolean; onClick: (id: string) => void }) {
  return (
    <div
      data-test={`category-${replaceSpaces(name)}`}
      className={`
        flex
        justify-center
        items-center
        h-10
        text-base
        px-2
        rounded
        cursor-pointer
        whitespace-nowrap
        ${isActive ? 'bg-primary' : 'hover:bg-grey-200'}
        ${isActive ? 'bg-primary' : 'bg-white'}
        ${isActive ? 'text-white' : 'text-grey-900'}
        ${isActive ? 'font-bold' : 'font-normal'}
      `}
      onClick={() => onClick(id)}>
      {leftIcon && (
        <div style={{ flex: '1 0 10%' }}>
          <Icon name={leftIcon} />
        </div>
      )}
      <div className="ml-1" style={{ flex: '4 1 90%' }}>
        {name}
      </div>
    </div>
  )
}

function Setting({
  action,
  description,
  name,
  postDescription,
  state,
  shownPostDescriptionName,
  attachedSettings,
  className,
  isPostDescriptionGrey = true,
}: Setting & { shownPostDescriptionName: string }) {
  return (
    <div className={cx('flex flex-col relative', className)} style={{ maxWidth: 950 }}>
      <div className="flex my-2">
        <div className="flex flex-col flex-grow ">
          <div className="text-lg leading-normal font-medium mb-3">{name}</div>
          <div className="text-sm text-grey-700">{description}</div>
        </div>
        <div className="flex flex-col items-end justify-between" style={{ minWidth: 120 }}>
          {action}
          {state}
        </div>
      </div>
      {attachedSettings && (
        <div className="bg-grey-100 rounded pl-5 pr-1 py-2 ml-3 -mr-1">{attachedSettings}</div>
      )}
      {postDescription && (
        <Collapse isOpen={name === shownPostDescriptionName}>
          <div
            className={cx('rounded pl-5 pr-1 py-2 ml-3 -mr-1', {
              'bg-grey-100': isPostDescriptionGrey,
            })}>
            {postDescription}
          </div>
        </Collapse>
      )}
      <Divider />
    </div>
  )
}

const omitByNull = omitBy(isNull)
